// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Button, IconButton, ProviderLogo, SearchBar, Skeleton, TablePagination } from '@loa/ui-components';
import { providerApi } from '../api/providerApi';
import { useAdvisor } from '../contexts/AdvisorContext';
import { AddAgencyCodeModal } from '../components/AddAgencyCodeModal';
import { formatAum, eurFormatter } from '../utils/currency';
import type { ProviderSummaryDTO } from '../types/provider';

// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function getProviderInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function AgencyCodeCell({ provider }: Readonly<{ provider: ProviderSummaryDTO }>) {
  if (!provider.effectiveAgencyCode) {
    return <span className="text-grey-03 italic">Not set</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-medium text-black">{provider.effectiveAgencyCode}</span>
      {provider.agencyCodeSource === 'ADVISOR' && (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-lilac-04 text-purple-primary text-body-03 font-medium whitespace-nowrap">
          Personal
        </span>
      )}
      {provider.agencyCodeSource === 'BROKERAGE' && (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-neutral-bg text-neutral-text text-body-03 font-medium whitespace-nowrap">
          Brokerage
        </span>
      )}
    </span>
  );
}

type SortField = 'name' | 'totalPolicies' | 'aum' | 'openLoas' | 'activeProfiles';
type SortDir = 'asc' | 'desc';

export function ProvidersListPage() {
  const navigate = useNavigate();
  const { advisor } = useAdvisor();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('openLoas');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [openLoasOnly, setOpenLoasOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Data state
  const [providers, setProviders] = useState<ProviderSummaryDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Add agency code modal state
  const [addCodeModalOpen, setAddCodeModalOpen] = useState(false);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Load providers
  const loadProviders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await providerApi.getProviders({
        search: debouncedSearch || undefined,
        sortBy: sortField,
        sortDirection: sortDir,
      });

      setProviders(result);
    } catch (err) {
      console.error('Failed to load providers:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, sortField, sortDir]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, sortField, sortDir, openLoasOnly, itemsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  // The list endpoint returns the full (advisor-scoped) provider set;
  // the open-LOAs filter and pagination are applied client-side.
  const filteredProviders = useMemo(() => {
    if (openLoasOnly) {
      return providers.filter((p) => p.openLoasCount > 0);
    }
    return providers;
  }, [providers, openLoasOnly]);

  // Aggregate stats for the tiles (across all providers, unfiltered)
  const stats = useMemo(
    () => ({
      totalProviders: providers.length,
      totalPolicies: providers.reduce((sum, p) => sum + (p.totalPoliciesCount || 0), 0),
      totalAum: providers.reduce((sum, p) => sum + (p.totalAum || 0), 0),
      openLoas: providers.reduce((sum, p) => sum + (p.openLoasCount || 0), 0),
    }),
    [providers]
  );

  const totalElements = filteredProviders.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / itemsPerPage));
  const pagedProviders = useMemo(
    () => filteredProviders.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage),
    [filteredProviders, currentPage, itemsPerPage]
  );

  const hasActiveFilters = Boolean(searchQuery.trim()) || openLoasOnly;

  const columns: Array<{ label: string; field: SortField | null; align?: 'right' | 'center' }> = [
    { label: 'Provider', field: 'name' },
    { label: 'Agency Code', field: null },
    { label: 'No. of policies', field: 'totalPolicies', align: 'center' },
    { label: 'AUM', field: 'aum', align: 'right' },
    { label: 'Open LOAs', field: 'openLoas', align: 'center' },
    { label: 'Active Relationships', field: 'activeProfiles', align: 'center' },
    { label: 'Actions', field: null, align: 'center' },
  ];

  return (
    <div className="pb-8 font-google">
      {/* Stats Cards Row */}
      <div className="flex justify-between items-start py-6 px-4 bg-white rounded-card shadow-card my-lg mx-lg">
        {/* Total Providers */}
        <div className="flex-1 px-4">
          <div className="text-body-03 text-black font-normal leading-normal">Total Providers</div>
          <div className="text-h4 font-medium text-success-text leading-tight mt-1">{stats.totalProviders}</div>
        </div>
        <div className="w-px bg-grey-05 self-stretch" />
        {/* Total Policies */}
        <div className="flex-1 px-4">
          <div className="text-body-03 text-black font-normal leading-normal">Total Policies</div>
          <div className="text-h4 font-medium text-black leading-tight mt-1">{stats.totalPolicies}</div>
        </div>
        <div className="w-px bg-grey-05 self-stretch" />
        {/* Total AUM */}
        <div className="flex-1 px-4">
          <div className="text-body-03 text-black font-normal leading-normal">Total AUM</div>
          <div className="text-h4 font-medium text-black leading-tight mt-1">{eurFormatter.format(stats.totalAum)}</div>
        </div>
        <div className="w-px bg-grey-05 self-stretch" />
        {/* Open LOAs — clickable filter tile, mirrors the Relationships health tiles */}
        <div
          className="flex-1 px-4 cursor-pointer"
          data-testid="stat-open-loas"
          onClick={() => setOpenLoasOnly((prev) => !prev)}
        >
          <div className="text-body-03 text-black font-normal leading-normal">
            Open LOAs{openLoasOnly ? ' (filtered)' : ''}
          </div>
          <div className={`text-h4 font-medium leading-tight mt-1 ${stats.openLoas > 0 ? 'text-warning-text' : 'text-black'}`}>
            {stats.openLoas}
          </div>
        </div>
      </div>

      {/* Main Providers Card */}
      <div className="bg-white rounded-card shadow-card overflow-hidden mx-lg">
        {/* Card Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="text-body-01 font-medium font-google text-black leading-normal">
            Providers
          </div>
        </div>

        {/* Search + Add Agency Code */}
        <div className="flex items-center gap-3 px-6 pb-5 justify-between">
          <div className="flex-1">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by provider name"
            />
          </div>
          <Button
            variant="filled"
            onClick={() => setAddCodeModalOpen(true)}
            iconLeft={<AppIcon name="plus" size={14} />}
            data-testid="add-agency-code-button"
          >
            Add Agency Code
          </Button>
        </div>

        {/* Table */}
        {(() => {
          if (isLoading) {
            return (
              <div className="px-6 pb-6" data-testid="providers-loading">
                <Skeleton variant="paragraph" lines={5} />
              </div>
            );
          }
          if (error) {
            return (
              <div className="px-6 pb-6">
                <Alert variant="error" header="Error Loading Providers">
                  <p>{error.message || 'Failed to load providers'}</p>
                  <Button variant="outlined" onClick={loadProviders}>
                    <AppIcon name="refresh" />
                    Retry
                  </Button>
                </Alert>
              </div>
            );
          }
          if (pagedProviders.length === 0) {
            return (
              <div className="mx-6 mb-6 rounded-xl bg-lilac-05 flex flex-col items-center justify-center py-8 gap-3">
                <span className="text-black text-body-02">
                  {hasActiveFilters
                    ? 'No providers match your search criteria. Try adjusting your filters.'
                    : 'No providers are available for your brokerage yet. Contact your brokerage admin to enable providers.'}
                </span>
              </div>
            );
          }
          return (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-lilac-05 font-medium">
                    {columns.map((col) => {
                      const isSortable = col.field !== null;
                      const isActive = isSortable && sortField === col.field;
                      let alignClass = 'text-left';
                      if (col.align === 'right') alignClass = 'text-right';
                      else if (col.align === 'center') alignClass = 'text-center';
                      return (
                        <th
                          key={col.label}
                          className={`px-4 py-3 ${alignClass} text-body-03 font-medium text-black font-google whitespace-nowrap ${isSortable ? 'cursor-pointer select-none hover:bg-lilac-04' : ''}`}
                          onClick={isSortable && col.field ? () => handleSort(col.field as SortField) : undefined}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {isSortable && (
                              <span
                                className={`inline-flex transition-transform ${isActive && sortDir === 'asc' ? 'rotate-180' : ''}`}
                              >
                                <AppIcon name="chevron down" size={14} />
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pagedProviders.map((provider) => (
                    <tr
                      key={provider.id}
                      className="border-t border-grey-06 hover:bg-page-bg transition-colors cursor-pointer"
                      onClick={() => navigate(`/providers/${provider.id}`)}
                      data-testid={`provider-row-${provider.id}`}
                    >
                      <td className="px-4 py-4 text-body-03 font-google text-black">
                        <div className="flex items-center gap-3">
                          {provider.logoUrl ? (
                            <ProviderLogo
                              logoUrl={provider.logoUrl}
                              name={provider.name}
                              size={32}
                              shape="rounded"
                            />
                          ) : (
                            <div
                              className="w-8 h-8 rounded-full bg-purple-primary text-white flex items-center justify-center text-body-03 font-medium shrink-0"
                              aria-hidden="true"
                            >
                              {getProviderInitials(provider.name)}
                            </div>
                          )}
                          <span className="font-medium" title={provider.name}>{provider.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-body-03 font-google text-black">
                        <AgencyCodeCell provider={provider} />
                      </td>
                      <td className="px-4 py-4 text-body-03 font-google text-black text-center">
                        {provider.totalPoliciesCount}
                      </td>
                      <td className="px-4 py-4 text-body-03 font-google text-black text-right font-medium whitespace-nowrap">
                        {formatAum(provider.totalAum)}
                      </td>
                      <td className="px-4 py-4 text-body-03 font-google text-black text-center">
                        {provider.openLoasCount}
                      </td>
                      <td className="px-4 py-4 text-body-03 font-google text-black text-center">
                        {provider.activeProfilesCount}
                      </td>
                      <td
                        className="px-4 py-4 text-center whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="inline-flex items-center gap-2">
                          <IconButton
                            variant="ghost"
                            size="sm"
                            icon={<AppIcon name="eye" size={16} />}
                            onClick={() => navigate(`/providers/${provider.id}`)}
                            title="View provider"
                            aria-label={`View details for ${provider.name}`}
                          />
                          <IconButton
                            variant="ghost"
                            size="sm"
                            icon={<AppIcon name="settings gear" size={16} />}
                            onClick={() => navigate(`/providers/${provider.id}?tab=settings`)}
                            title="Edit provider settings"
                            aria-label={`Edit settings for ${provider.name}`}
                            data-testid={`provider-settings-${provider.id}`}
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Pagination */}
        {!isLoading && !error && totalElements > 0 && (
          <TablePagination
            config={{
              currentPage: currentPage + 1,
              totalPages,
              itemsPerPage,
              itemsPerPageOptions: [5, 10, 20, 50],
              onPageChange: (next) => setCurrentPage(next - 1),
              onItemsPerPageChange: (size) => {
                setItemsPerPage(size);
                setCurrentPage(0);
              },
            }}
          />
        )}
      </div>

      {/* Add agency code modal — covers providers the advisor has no policies
          with yet (the table only lists providers with at least one policy) */}
      {advisor?.id && advisor?.brokerageId && (
        <AddAgencyCodeModal
          open={addCodeModalOpen}
          onClose={() => setAddCodeModalOpen(false)}
          onSuccess={() => {
            setAddCodeModalOpen(false);
            void loadProviders();
          }}
          advisorId={String(advisor.id)}
          brokerageId={String(advisor.brokerageId)}
          existingProviderIds={providers.filter((p) => p.advisorAgencyCode).map((p) => p.id)}
        />
      )}
    </div>
  );
}
